/**
 * @file Wait until the element's light-DOM children have been parsed. SE-1004.
 *
 * Custom elements upgrade at different times depending on when their
 * definition lands:
 *
 *   - Defined *after* the document is parsed (the lazy-loading default) --
 *     upgrade happens with all light-DOM children already in place, so reading
 *     `this.children` in firstUpdated() works.
 *   - Defined *before* the parser reaches the tag (the critical bundle) --
 *     the element upgrades as it is parsed, so firstUpdated() can run while
 *     the element is still empty. `this.querySelector('nav')` returns null and
 *     any code that assumes otherwise throws, silently killing the component's
 *     enhancement.
 *
 * The second case is what defining components early buys us for layout
 * stability, so any component in the critical bundle that reads its own
 * light-DOM children must await this first.
 *
 * Waiting for DOMContentLoaded is deliberately coarse: it is correct for every
 * case, and it does not delay anything relative to the previous behaviour --
 * the lazy path already ran this work after parsing had finished.
 */
export function whenParsed(): Promise<void> {
  if (document.readyState !== 'loading') {
    return Promise.resolve();
  }

  return new Promise<void>(resolve => {
    document.addEventListener('DOMContentLoaded', () => resolve(), {
      once: true,
    });
  });
}
